import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'

import { historyUtils } from '@wartek-id/fe-toolbox'

import {
  EmptyStateAction,
  EmptyStateImage,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import { LayoutWithHeader as Layout } from 'components/Layout'
import { SessionError, CustomError } from 'utils/customError'
import { ForbiddenError } from 'app/ConversationPage/utils/customError'
import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'

interface ICustomErrorBoundaryProps {
  children: React.ReactNode
  resetKeys: string[]
  onError: (error: Error) => any
}
const CustomErrorBoundary = ({
  resetKeys,
  children,
  onError,
}: ICustomErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      resetKeys={resetKeys}
      FallbackComponent={CustomErrorFallback}
      onError={onError}
    >
      {children}
    </ErrorBoundary>
  )
}

export function CustomErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  const androidHandler = AndroidHandler()
  const router = useRouter()
  const { login } = useAuth()

  if (error instanceof CustomError === false) {
    throw error
  }

  // @ts-expect-error error is not an Error object
  const { title, description } = error

  return (
    <Layout
      title=""
      onClickBack={() =>
        historyUtils.backToApp(() => {
          resetErrorBoundary()
          router.back()
        })
      }
    >
      <div className="flex items-center h-screen px-12 mt-12">
        <Head>
          <title>{title}</title>
        </Head>
        <EmptyStateWithIllustration
          title={title}
          description={description}
          fullscreen={false}
          className="w-full text-center"
        >
          {error instanceof SessionError && (
            <>
              <EmptyStateImage
                data-testid="error-boundary-image"
                imageSrc="/images/errors/img-session-expired.png"
              />
              <EmptyStateAction
                data-testid="error-boundary-action-primary"
                color="black"
                onClick={() => {
                  androidHandler.backToHomepage(login)
                  resetErrorBoundary()
                }}
              >
                {androidHandler.enabled ? 'Kembali ke Beranda' : 'Login'}
              </EmptyStateAction>
            </>
          )}
          {error instanceof ForbiddenError && (
            <>
              <EmptyStateImage
                data-testid="error-boundary-image"
                imageSrc="/images/errors/img-no-access.png"
              />
              <EmptyStateAction
                data-testid="error-boundary-action-primary"
                color="black"
                onClick={() => {
                  androidHandler.backToHomepage(() => router.push('/home'))
                }}
              >
                Kembali ke Beranda
              </EmptyStateAction>
            </>
          )}
        </EmptyStateWithIllustration>
      </div>
    </Layout>
  )
}

export default CustomErrorBoundary
